import React from 'react';

/**
 * Thin-line icon set (1.5px stroke, 24×24 viewbox) — matches DESIGN.md spec.
 * Mirrors the icons used in app/src/components/Icon.tsx.
 */
interface IconProps {
  size?: number;
  color?: string;
  className?: string;
  strokeWidth?: number;
}

const base = (size: number, color: string, strokeWidth: number, children: React.ReactNode, className?: string) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    {children}
  </svg>
);

export const Car: React.FC<IconProps> = ({ size = 24, color = '#0F0F0E', strokeWidth = 1.5, className }) =>
  base(
    size,
    color,
    strokeWidth,
    <>
      <path d="M3 13l1.5-5A2 2 0 0 1 6.4 6.5h11.2a2 2 0 0 1 1.9 1.5L21 13" />
      <path d="M2 13h20v5a1 1 0 0 1-1 1h-2a1 1 0 0 1-1-1v-1H6v1a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-5z" />
      <circle cx="7" cy="16" r="1" />
      <circle cx="17" cy="16" r="1" />
    </>,
    className
  );

export const Search: React.FC<IconProps> = ({ size = 24, color = '#0F0F0E', strokeWidth = 1.5, className }) =>
  base(
    size,
    color,
    strokeWidth,
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.3-4.3" />
    </>,
    className
  );

export const ChevronRight: React.FC<IconProps> = ({ size = 18, color = '#8B8680', strokeWidth = 1.5, className }) =>
  base(size, color, strokeWidth, <path d="M9 6l6 6-6 6" />, className);

export const ChevronLeft: React.FC<IconProps> = ({ size = 18, color = '#0F0F0E', strokeWidth = 1.5, className }) =>
  base(size, color, strokeWidth, <path d="M15 6l-6 6 6 6" />, className);

export const Plus: React.FC<IconProps> = ({ size = 18, color = '#0F0F0E', strokeWidth = 1.5, className }) =>
  base(
    size,
    color,
    strokeWidth,
    <>
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </>,
    className
  );

export const Check: React.FC<IconProps> = ({ size = 18, color = '#2D5A3D', strokeWidth = 1.5, className }) =>
  base(size, color, strokeWidth, <path d="M5 12l5 5L20 7" />, className);

export const Settings: React.FC<IconProps> = ({ size = 18, color = '#0F0F0E', strokeWidth = 1.5, className }) =>
  base(
    size,
    color,
    strokeWidth,
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9c.06.36.27.69.59.93h.01A2 2 0 0 1 21 12a2 2 0 0 1-1 1.73h-.01a1.65 1.65 0 0 0-.59.93z" />
    </>,
    className
  );

export const Clock: React.FC<IconProps> = ({ size = 20, color = '#4D4B47', strokeWidth = 1.5, className }) =>
  base(
    size,
    color,
    strokeWidth,
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>,
    className
  );

export const User: React.FC<IconProps> = ({ size = 18, color = '#0F0F0E', strokeWidth = 1.5, className }) =>
  base(
    size,
    color,
    strokeWidth,
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" />
    </>,
    className
  );

export const Camera: React.FC<IconProps> = ({ size = 24, color = '#0F0F0E', strokeWidth = 1.5, className }) =>
  base(
    size,
    color,
    strokeWidth,
    <>
      <path d="M3 7h3l2-3h8l2 3h3a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2z" />
      <circle cx="12" cy="13" r="4" />
    </>,
    className
  );

export const Share: React.FC<IconProps> = ({ size = 18, color = '#0F0F0E', strokeWidth = 1.5, className }) =>
  base(
    size,
    color,
    strokeWidth,
    <>
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <path d="M8.6 10.5l6.8-4M8.6 13.5l6.8 4" />
    </>,
    className
  );

export const Navigate: React.FC<IconProps> = ({ size = 18, color = '#0F0F0E', strokeWidth = 1.5, className }) =>
  base(size, color, strokeWidth, <path d="M3 11l18-8-8 18-2-8-8-2z" />, className);

export const Alert: React.FC<IconProps> = ({ size = 24, color = '#B0533A', strokeWidth = 1.5, className }) =>
  base(
    size,
    color,
    strokeWidth,
    <>
      <path d="M10.3 3.86L1.82 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.86a2 2 0 0 0-3.4 0z" />
      <path d="M12 9v4" />
      <path d="M12 17h0" />
    </>,
    className
  );

export const Compass: React.FC<IconProps> = ({ size = 18, color = '#0F0F0E', strokeWidth = 1.5, className }) =>
  base(
    size,
    color,
    strokeWidth,
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M16 8l-2.5 7.5L6 18l2.5-7.5L16 8z" />
    </>,
    className
  );

export const Trash: React.FC<IconProps> = ({ size = 18, color = '#0F0F0E', strokeWidth = 1.5, className }) =>
  base(
    size,
    color,
    strokeWidth,
    <>
      <path d="M3 6h18" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <path d="M19 6l-1.6 13.6A2 2 0 0 1 15.4 21H8.6a2 2 0 0 1-2-1.4L5 6" />
    </>,
    className
  );

export const LocationPulse: React.FC<IconProps> = ({ size = 14, color = '#0F0F0E', strokeWidth = 1.5, className }) =>
  base(
    size,
    color,
    strokeWidth,
    <>
      <circle cx="12" cy="12" r="3" />
      <circle cx="12" cy="12" r="8" opacity={0.4} />
    </>,
    className
  );

export const Mail: React.FC<IconProps> = ({ size = 16, color = '#0F0F0E', strokeWidth = 1.5, className }) =>
  base(
    size,
    color,
    strokeWidth,
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 7l9 6 9-6" />
    </>,
    className
  );
